import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import { useIsMobileView } from "@/contexts/ResponsiveProvider";
import { Input } from "@/v2-components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/v2-components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/v2-components/ui/sheet";
import {
  CountryOption,
  flagUrl,
  PartnerOption,
  partnerColor,
} from "../_constants/where-to-try-constants";

interface PartnerFilterProps {
  // 호출처가 **현재 국가 범위**로 좁혀 넘긴다(전역 파트너 목록 아님) — count 도 그 범위 기준.
  // 범위 밖 파트너를 넘기면 사용자가 빈 조합(국가 × 매장 없는 파트너)을 고를 수 있다.
  // 아래 `partners.length > 1` 게이트 때문에, 범위 내 파트너가 1곳이면 탭 바 자체가 렌더되지 않는다.
  partners: PartnerOption[];
  selectedPartnerId: string | null;
  onSelectPartner: (id: string | null) => void;
  // 국가 우선 진입 — 매장 보유 국가만. Radix Select 는 메뉴 내 검색 불가 → Popover+Input 콤보박스.
  countries: CountryOption[];
  selectedCountry: string | null;
  onSelectCountry: (code: string) => void;
  // "Use my location": 정밀 좌표로 최근접 정렬. active = 정밀 좌표 확보됨.
  located: boolean;
  onUseMyLocation: () => void;
  geoError: string | null;
  // location bar 우측 요약("N venues in {country}"). 프로토 .summary(margin-left:auto).
  summary?: React.ReactNode;
  // 증가하면 국가 콤보박스를 연다("Choose another country" 링크 → 프로토 openMenu).
  openToken?: number;
}

export default function PartnerFilter({
  partners,
  selectedPartnerId,
  onSelectPartner,
  countries,
  selectedCountry,
  onSelectCountry,
  located,
  onUseMyLocation,
  geoError,
  summary,
  openToken,
}: PartnerFilterProps) {
  const { t } = useTrans(["where-to-try"]);

  return (
    <div className="flex w-full flex-col gap-4">
      {/* location bar: 국가 콤보박스 + Use my location(아이콘) + 요약(우측). 자유 검색창 없음(프로토 v4). */}
      <div className="flex w-full flex-wrap items-center gap-3">
        <CountryCombobox
          countries={countries}
          selected={selectedCountry}
          onSelect={onSelectCountry}
          openToken={openToken}
        />

        <button
          type="button"
          onClick={onUseMyLocation}
          aria-pressed={located}
          title={
            located
              ? (t("Sorted by nearest to you") as string)
              : (t("Use my location") as string)
          }
          aria-label={
            located
              ? (t("Sorted by nearest to you") as string)
              : (t("Use my location") as string)
          }
          className={cn(
            "inline-flex size-12 shrink-0 items-center justify-center rounded-lg transition-colors",
            located
              ? "bg-blue-10 text-blue-60"
              : "text-coolgray-50 hover:bg-coolgray-10 hover:text-coolgray-90",
          )}
        >
          <LocationIcon className="size-5" />
        </button>

        {summary && (
          // 요약("N venues · M partners in {country}")은 모바일 숨김, 데스크톱만 우측 노출(Figma).
          <span className="hidden text-sm text-coolgray-60 smd:ml-auto smd:block">
            {summary}
          </span>
        )}
      </div>

      {geoError && (
        <p role="status" className="-mt-2 text-xs text-orange-50">
          {t(geoError)}
        </p>
      )}

      {/* 범위 내 파트너 2곳 이상일 때만(1곳이면 필터가 무의미 — prop 주석 참조).
          데스크톱=밑줄 탭 바, 모바일=가로 rail(pitfalls #14 shrink-0).

          ★`scrollbar-none` — rail 이 넘칠 때 회색 막대가 아래 `border-b` 와 겹쳐
          보인다 (#5712 R1 M1). 같은 클래스가 게임 필터 시트에서 신고됐다(#5709).
          ⚠️ 클래식 스크롤바 환경(Windows 등)에서만 상시 노출된다 — macOS 기본은
          오버레이라 개발 중에 안 보인다. */}
      {partners.length > 1 && (
        <div className="scrollbar-none -mx-4 flex items-center gap-6 overflow-x-auto border-b border-coolgray-20 px-4 smd:mx-0 smd:px-0">
          <TabChip
            active={selectedPartnerId === null}
            onClick={() => onSelectPartner(null)}
          >
            {t("All partners")}
          </TabChip>
          {partners.map((p) => (
            <TabChip
              key={p.id}
              active={selectedPartnerId === p.id}
              onClick={() => onSelectPartner(p.id)}
              dotColor={partnerColor(p.id)}
            >
              {p.name} ({p.count})
            </TabChip>
          ))}
        </div>
      )}
    </div>
  );
}

function CountryCombobox({
  countries,
  selected,
  onSelect,
  openToken,
}: {
  countries: CountryOption[];
  selected: string | null;
  onSelect: (code: string) => void;
  openToken?: number;
}) {
  const { t } = useTrans(["where-to-try"]);
  // 모바일은 하단 Sheet — 검색칸은 있으나 열 때 검색칸으로 자동 포커스하지 않는다. 팝오버+검색칸
  // autoFocus 로 가상 키보드가 즉시 떠 지도를 가리던 문제(#5030)를 회피하기 위함. Radix Dialog 는
  // 기본적으로 열릴 때 첫 포커스 요소(=검색 input)로 포커스를 옮기므로 그대로 두면 키보드가 뜬다.
  // → onOpenAutoFocus 에서 preventDefault 로 기본 이동을 막되, 포커스를 input 이 아닌 제목(비-입력
  //   요소, tabIndex=-1)으로 명시 이동한다. 이렇게 하면 (1) 키보드는 안 뜨고 (2) 포커스가 다이얼로그
  //   안으로 들어가 스크린리더가 시트 열림을 announce 한다(포커스가 트리거에 남으면 SR 인지 갭 발생).
  // 사용자가 검색칸을 직접 탭할 때만 키보드가 뜬다. 데스크톱은 Popover+검색(autoFocus) 그대로 유지.
  const isMobile = useIsMobileView();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // 시트 열릴 때 포커스를 옮길 대상(제목). input 이 아니라 키보드는 안 뜨고, 다이얼로그 내부라 SR announce.
  const sheetTitleRef = useRef<HTMLHeadingElement>(null);

  // openToken 증가 시 열기(초기 마운트는 스킵). "Choose another country" 링크용.
  const firstToken = useRef(true);
  useEffect(() => {
    if (firstToken.current) {
      firstToken.current = false;
      return;
    }
    setOpen(true);
  }, [openToken]);

  const current = useMemo(
    () => countries.find((c) => c.code === selected) ?? null,
    [countries, selected],
  );
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter((c) => c.name.toLowerCase().includes(q));
  }, [countries, query]);

  const handleSelect = (code: string) => {
    onSelect(code);
    setOpen(false);
    // setOpen(false) 는 Radix onOpenChange 를 거치지 않으므로(외부 open prop 변경) 여기서 직접
    // 검색어를 초기화한다. 안 그러면 선택으로 닫은 뒤 다시 열 때 이전 검색어가 남는다.
    setQuery("");
  };

  const emptyText = t("No matching countries with venues.") as string;

  const trigger = (
    <button
      type="button"
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-label={t("Choose a country") as string}
      // 모바일/태블릿(≤1023): flex-1 + locate 버튼과 한 줄. 데스크톱(≥1024): 288px 고정.
      // 너비 경계(laptop)를 Sheet↔Popover 분기(useIsMobileView=≤1023)와 일치시킴 — 태블릿에서
      // 바텀시트+288px 트리거가 섞이지 않도록.
      className="inline-flex h-12 min-w-0 flex-1 items-center gap-2 rounded border border-coolgray-20 bg-white px-4 text-sm font-500 text-coolgray-100 transition-colors hover:bg-coolgray-10 laptop:w-[288px] laptop:flex-none"
    >
      {current ? (
        <>
          <Flag code={current.code} />
          <span className="truncate">{current.name}</span>
          <span className="text-coolgray-50">({current.count})</span>
        </>
      ) : (
        <span className="text-coolgray-50">{t("Choose a country")}</span>
      )}
      <ChevronIcon
        className={cn(
          "ml-auto size-4 text-coolgray-50 transition-transform",
          open && "rotate-180",
        )}
      />
    </button>
  );

  // ── 모바일: 하단 Sheet (검색 있음 · 자동 포커스 없음) ──
  // 높이는 repo sheetVariants bottom 기본값 h-[90%] 을 그대로 쓴다(= "Your Bag" 시트와 동일).
  // max-height 매직넘버(예: 80%)를 새로 도입하지 않는다 — 검색 결과가 줄어도 시트 높이는 고정,
  // 안쪽 리스트만 스크롤한다.
  if (isMobile) {
    return (
      <Sheet
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setQuery("");
        }}
      >
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent
          side="bottom"
          aria-describedby={undefined}
          // 열릴 때 Radix 기본 자동 포커스(=검색 input) 차단 → 키보드 안 뜸(#5030 회귀 방지). 대신
          // 포커스를 제목으로 옮겨 다이얼로그 안으로 넣는다(SR announce 유지, 키보드는 여전히 안 뜸).
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            sheetTitleRef.current?.focus();
          }}
          className="flex flex-col gap-0 rounded-t-2xl p-0"
        >
          {/* 그립 핸들(Figma "Bottom Sheet › Header": 28px 영역 · 40×4 grip). 시각 어포던스 + 스와이프
              다운 닫기 힌트. X 버튼은 없다 — 오버레이 탭 / Esc 로 닫힘(Radix Dialog 기본). */}
          <div
            aria-hidden="true"
            className="flex h-7 shrink-0 items-center justify-center"
          >
            <span className="h-1 w-10 rounded-full bg-coolgray-20" />
          </div>
          {/* mt-0 으로 SheetHeader 기본 -mt-4(음수 마진)를 무효화한다 — 안 그러면 타이틀이 위로 끌려
              올라가 그립 바와 겹쳐 "간격 없음"으로 보인다. 그립 컨테이너(h-7=28px, 바 중앙정렬)가 바 하단에
              이미 12px 여백을 가지므로 pt-0 이면 보이는 그립 바↔타이틀 = 12px(디자이너 요청). */}
          <SheetHeader className="mt-0 shrink-0 space-y-0 px-4 pb-2 pt-0 text-left">
            {/* tabIndex=-1: 열릴 때 onOpenAutoFocus 가 여기로 포커스를 옮긴다(SR announce). 프로그래매틱
                포커스라 outline-none 로 시각 링 억제(키보드 포커스 이동엔 링이 안 뜸). */}
            <SheetTitle
              ref={sheetTitleRef}
              tabIndex={-1}
              className="text-[15px] font-600 text-coolgray-90 outline-none"
            >
              {t("Choose a country")}
            </SheetTitle>
          </SheetHeader>
          {/* 검색 input: font-size 16px 강제(iOS 포커스 시 줌 방지). 이 프로젝트 Tailwind 스케일에선
              text-base=14px 라 repo Input 기본값으로는 16px 이 안 나온다 → text-[16px] 로 못박는다.
              repo Input 이 focus outline 2px blue-60 은 이미 제공. autoFocus 는 주지 않는다(위
              onOpenAutoFocus 와 함께 키보드 억제). */}
          <div className="shrink-0 px-4 pb-2">
            <Input
              type="text"
              value={query}
              placeholder={t("Search country…") as string}
              aria-label={t("Search country…") as string}
              autoComplete="off"
              onChange={(e) => setQuery(e.target.value)}
              className="h-12 py-0 text-[16px]"
            />
          </div>
          <CountryList
            items={results}
            selected={selected}
            onSelect={handleSelect}
            emptyText={emptyText}
            variant="sheet"
            className="flex-1 pb-[max(env(safe-area-inset-bottom),16px)]"
          />
        </SheetContent>
      </Sheet>
    );
  }

  // ── 데스크톱: Popover + 검색 콤보박스 ──
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery("");
      }}
    >
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" className="w-[288px] p-0">
        <div className="px-4 pb-1 pt-2 text-xs font-600 text-coolgray-50">
          {t("Choose a country")}
        </div>
        <div className="px-3 pb-2">
          <Input
            autoFocus
            value={query}
            placeholder={t("Search country…") as string}
            onChange={(e) => setQuery(e.target.value)}
            className="min-h-10 rounded py-2 text-sm"
          />
        </div>
        <CountryList
          items={results}
          selected={selected}
          onSelect={handleSelect}
          emptyText={emptyText}
          className="max-h-[300px]"
        />
      </PopoverContent>
    </Popover>
  );
}

// 국가 옵션 리스트. Popover(데스크톱, variant="popover")·Sheet(모바일, variant="sheet") 양쪽에서 공유.
// 두 표면의 행 치수가 다르다(Figma): sheet 는 터치 타깃이 더 크다(min-h 52 · flag 22×16 · 15px/13px ·
// 선택 행 blue-10 배경 + 18px 체크). popover 는 기존 컴팩트(min-h 44 · flag 20×14 · 14px · coolgray-10 선택).
function CountryList({
  items,
  selected,
  onSelect,
  emptyText,
  className,
  variant = "popover",
}: {
  items: CountryOption[];
  selected: string | null;
  onSelect: (code: string) => void;
  emptyText: string;
  className?: string;
  variant?: "popover" | "sheet";
}) {
  const sheet = variant === "sheet";
  return (
    <ul className={cn("overflow-y-auto pb-1", className)} role="listbox">
      {items.length === 0 ? (
        <li
          className={cn(
            "text-sm text-coolgray-50",
            sheet ? "px-4 py-4" : "px-4 py-3",
          )}
        >
          {emptyText}
        </li>
      ) : (
        items.map((c) => (
          <li key={c.code}>
            <button
              type="button"
              role="option"
              aria-selected={c.code === selected}
              onClick={() => onSelect(c.code)}
              className={cn(
                "flex w-full items-center px-4 text-left transition-colors",
                sheet
                  ? "min-h-[52px] gap-3 text-[15px]"
                  : "min-h-11 gap-2.5 text-sm",
                c.code === selected
                  ? sheet
                    ? "bg-blue-10 text-coolgray-90"
                    : "bg-coolgray-10 text-coolgray-100"
                  : sheet
                    ? "text-coolgray-90 hover:bg-coolgray-10"
                    : "text-coolgray-100 hover:bg-coolgray-10",
              )}
            >
              <Flag code={c.code} size={sheet ? "lg" : "md"} />
              <span className="flex-1 truncate">{c.name}</span>
              {c.code === selected ? (
                <CheckIcon
                  className={cn(
                    "text-blue-60",
                    sheet ? "size-[18px]" : "size-4",
                  )}
                />
              ) : (
                <span
                  className={cn("text-coolgray-50", sheet && "text-[13px]")}
                >
                  {c.count}
                </span>
              )}
            </button>
          </li>
        ))
      )}
    </ul>
  );
}

function Flag({ code, size = "md" }: { code: string; size?: "md" | "lg" }) {
  const lg = size === "lg";
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={flagUrl(code)}
      alt=""
      width={lg ? 22 : 20}
      height={lg ? 16 : 14}
      className={cn(
        "shrink-0 rounded-sm object-cover",
        lg ? "h-4 w-[22px]" : "h-[14px] w-[20px]",
      )}
      loading="lazy"
    />
  );
}

// 밑줄 탭(데스크톱)/가로 rail(모바일). active=색 변화만(weight 고정 → 폭 reflow 없음, pitfalls #15).
function TabChip({
  active,
  onClick,
  children,
  dotColor,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  dotColor?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="tab"
      aria-selected={active}
      className={cn(
        "-mb-px flex h-10 shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-0.5 text-sm font-500 transition-colors",
        active
          ? "border-blue-60 text-coolgray-100"
          : "border-transparent text-coolgray-60 hover:text-coolgray-100",
      )}
    >
      {dotColor && (
        <span
          aria-hidden="true"
          className="size-2 rounded-full"
          style={{ backgroundColor: dotColor }}
        />
      )}
      {children}
    </button>
  );
}

// 프로토 v4: Use my location = 크로스헤어(target) 아이콘(위치 핀 아님).
function LocationIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <circle cx="12" cy="12" r="7" />
      <line x1="12" y1="1" x2="12" y2="4" />
      <line x1="12" y1="20" x2="12" y2="23" />
      <line x1="1" y1="12" x2="4" y2="12" />
      <line x1="20" y1="12" x2="23" y2="12" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
